import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader, KanbanSquare, AlertCircle } from 'lucide-react';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { kanbanApiService } from '../../services/kanbanApi';
import type { KanbanBoard } from '../../types/kanban';
import { cn } from '../../utils/cn';

interface EditBoardModalProps {
  isOpen: boolean;
  onClose: () => void;
  board: KanbanBoard;
  onUpdated: (board: KanbanBoard) => void;
}

export const EditBoardModal = ({ isOpen, onClose, board, onUpdated }: EditBoardModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(modalRef, onClose);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [backgroundColor, setBackgroundColor] = useState('#334155');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setIsSubmitting(false);
    setTitle(board.title);
    setDescription(board.description ?? '');
    setBackgroundColor(board.background_color?.trim() || '#334155');
  }, [isOpen, board]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      setError('Please enter a board title.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const updated = await kanbanApiService.updateBoard(board.id, {
        title: trimmedTitle,
        description: description.trim() || null,
        background_color: backgroundColor.trim() || null,
      });
      onUpdated(updated);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update board.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="modal-overlay z-[9999]">
      <div
        ref={modalRef}
        className="modal-panel max-w-lg"
      >
        <button
          type="button"
          onClick={onClose}
          className="modal-close touch-manipulation z-10"
          aria-label="Close"
          disabled={isSubmitting}
        >
          <X className="w-5 h-5" />
        </button>

        <div className="modal-header">
          <h2 className="modal-title flex items-center gap-2">
            <KanbanSquare className="w-5 h-5 text-accent-400 shrink-0" />
            Edit board
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="alert alert-danger" role="alert">
              <AlertCircle />
              <span>{error}</span>
            </div>
          )}

          <div>
            <label htmlFor="edit-board-title" className="text-xs font-medium text-glass-400 mb-1.5 block">
              Title
            </label>
            <input
              id="edit-board-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="glass-input w-full"
              disabled={isSubmitting}
              required
            />
          </div>

          <div>
            <label htmlFor="edit-board-description" className="text-xs font-medium text-glass-400 mb-1.5 block">
              Description
            </label>
            <textarea
              id="edit-board-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="glass-input w-full min-h-[80px] resize-y"
              rows={3}
              disabled={isSubmitting}
            />
          </div>

          <div>
            <label htmlFor="edit-board-bg" className="text-xs font-medium text-glass-400 mb-1.5 block">
              Background color
            </label>
            <div className="flex items-center gap-3">
              <input
                id="edit-board-bg"
                type="color"
                value={backgroundColor.startsWith('#') ? backgroundColor.slice(0, 7) : '#334155'}
                onChange={(e) => setBackgroundColor(e.target.value)}
                className="h-10 w-14 cursor-pointer rounded-lg border border-glass-200/20 bg-transparent p-0.5"
                disabled={isSubmitting}
                aria-label="Background color"
              />
              <input
                type="text"
                value={backgroundColor}
                onChange={(e) => setBackgroundColor(e.target.value)}
                className="glass-input flex-1 font-mono"
                disabled={isSubmitting}
              />
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              onClick={onClose}
              className="glass-button-secondary"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={cn('btn-primary', isSubmitting && 'opacity-70')}
              disabled={isSubmitting}
            >
              {isSubmitting ? <Loader className="w-4 h-4 animate-spin" /> : null}
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};
